import { User } from 'firebase/auth';
import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import log from 'loglevel';
import { FIREBASE_API_KEY, NODE_ENV } from '../../constants';
import { signInAuthCustomToken, signInEmail, getAuthUserMock } from '../authModule';

interface AuthState {
  isSigning: boolean;
  user: User | null;
  signInWithCustomToken: (customToken: string) => Promise<User | undefined>;
  signInWithEmail: (email: string, password: string) => Promise<User | undefined>;
}

export const AuthContext = createContext<AuthState>({
  isSigning: false,
  user: null,
  signInWithCustomToken: async () => undefined,
  signInWithEmail: async () => undefined,
});

export function AuthProvider(props: Readonly<{ children: React.ReactNode }>) {
  const [user, setUser] = useState<User | null>(null);
  const [isSigning, setIsSigning] = useState(true);

  const signInWithCustomToken = useCallback(async (customToken: string) => {
    if (NODE_ENV === 'development' && !FIREBASE_API_KEY) {
      const user = getAuthUserMock();
      setUser(user);
      return user;
    }

    try {
      setIsSigning(true);
      const user = await signInAuthCustomToken(customToken);
      setUser(user);
      setIsSigning(false);
      return user;
    } catch (e) {
      log.error(`Could not sign in. ${e}`);
      setIsSigning(false);
    }
  }, []);

  const signInWithEmail = useCallback(async (email: string, password: string) => {
    if (NODE_ENV === 'development' && !FIREBASE_API_KEY) {
      const user = getAuthUserMock();
      setUser(user);
      return user;
    }

    try {
      setIsSigning(true);
      const user = await signInEmail(email, password);
      setUser(user);
      setIsSigning(false);
      return user;
    } catch (e) {
      log.error(`Could not sign in. ${e}`);
      setIsSigning(false);
    }
  }, []);

  const value = useMemo(
    () => ({ user, isSigning, signInWithCustomToken, signInWithEmail }),
    [user, isSigning, signInWithCustomToken, signInWithEmail],
  );

  return <AuthContext.Provider value={value} {...props} />;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within a AuthProvider');
  }
  return context;
}
